import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import {
  FREQUENCIES,
  calculateAmountFinanced,
  getApplicableTaxRate,
  findRateForTerm,
  REFERENCE_TERM_RATES,
  calculatePayment,
  isGapEligible,
  isWarrantyEligible,
  getGapCost,
  getWarrantyCost,
  calculateNegativeEquityImpact,
  ROADSIDE_ASSISTANCE_COST,
  ROAD_HAZARD_TIRE_COST,
  VEHICLE_REPLACEMENT_COST,
  VEHICLE_REPLACEMENT_ENABLED,
  formatCAD,
  formatRate,
} from '../../containers/PaymentCalculator/calculationEngine';
import { PrimaryButton, FinanceLeadForm } from '..';

import css from './FinanceTab.module.css';

const TERM_OPTIONS = [60, 72, 84, 96];

export default function FinanceTab({
  vehiclePrice,
  vehicleYear,
  vehicleKm,
  vehicleCategory,
  province,
  sellerType,
  listingId,
  lenderRates,
  inServiceDate,
  historyScreen = null,
}) {
  const [termMonths, setTermMonths] = useState(84);
  const [frequency, setFrequency] = useState('biweekly');
  const [downPayment, setDownPayment] = useState(0);
  const [tradeValue, setTradeValue] = useState(0);
  const [tradePayout, setTradePayout] = useState(0);
  const [warrantyOn, setWarrantyOn] = useState(false);
  const [gapOn, setGapOn] = useState(false);
  const [roadsideOn, setRoadsideOn] = useState(false);
  const [tireOn, setTireOn] = useState(false);
  const [replacementOn, setReplacementOn] = useState(false);
  const [historyConfirmed, setHistoryConfirmed] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);

  const taxRate = getApplicableTaxRate(province, sellerType);
  const tax = vehiclePrice * taxRate;

  const baseAmountFinanced = calculateAmountFinanced({
    vehiclePrice,
    tradeValue,
    tradePayout,
    downPayment,
    province,
    sellerType,
  });

  const gapEligible = isGapEligible(baseAmountFinanced);
  const warrantyEligible = isWarrantyEligible(inServiceDate, vehicleKm);
  const warrantyCost = getWarrantyCost(vehiclePrice);
  const gapCost = getGapCost(vehiclePrice);

  const protections = [
    warrantyEligible && warrantyOn ? { name: 'FinanceTab.warranty', cost: warrantyCost } : null,
    gapEligible && gapOn ? { name: 'FinanceTab.gap', cost: gapCost } : null,
    roadsideOn ? { name: 'FinanceTab.roadside', cost: ROADSIDE_ASSISTANCE_COST } : null,
    tireOn ? { name: 'FinanceTab.roadHazardTire', cost: ROAD_HAZARD_TIRE_COST } : null,
    VEHICLE_REPLACEMENT_ENABLED && replacementOn
      ? { name: 'FinanceTab.vehicleReplacement', cost: VEHICLE_REPLACEMENT_COST }
      : null,
  ].filter(Boolean);
  const protectionsTotal = protections.reduce((sum, p) => sum + p.cost, 0);

  const amountFinanced = baseAmountFinanced + protectionsTotal;

  // Rate varies by term -- longer terms carry a higher rate, same as real
  // lender/dealer rate sheets. Prefer a real matched lender rate for that
  // specific term; fall back to the reference dealer-rate assumptions
  // (labeled as an estimate in the UI, never presented as a live quote).
  const quotes = TERM_OPTIONS.map(termOption => {
    const matched = lenderRates?.length
      ? findRateForTerm(
          {
            category: vehicleCategory,
            vehicleYear,
            vehicleKm,
            amountFinanced,
            creditTier: 'B',
            province,
          },
          lenderRates,
          termOption
        )
      : null;
    const termRate = matched ?? REFERENCE_TERM_RATES[termOption];
    return {
      termMonths: termOption,
      rate: termRate,
      frequency,
      ...calculatePayment(amountFinanced, termRate, termOption, frequency),
    };
  });
  const selectedQuote = quotes.find(q => q.termMonths === termMonths) || quotes[0];

  const { negativeEquity, paymentImpact } = calculateNegativeEquityImpact({
    tradeValue,
    tradePayout,
    rate: selectedQuote.rate,
    termMonths,
    frequency,
  });

  const purchaseOptionSnapshot = {
    tab: 'finance',
    termMonths,
    rate: selectedQuote?.rate,
    frequency,
    payment: selectedQuote?.payment,
  };
  const estimateSnapshot = {
    tax,
    taxRate,
    amountFinanced,
    protectionsTotal,
  };

  return (
    <div className={css.root}>
      <div className={css.termCards}>
        {quotes.map(q => (
          <button
            key={q.termMonths}
            type="button"
            className={`${css.termCard} ${q.termMonths === termMonths ? css.termCardSelected : ''}`}
            onClick={() => setTermMonths(q.termMonths)}
          >
            <span className={css.termCardMonths}>
              <FormattedMessage id="FinanceTab.termMonths" values={{ months: q.termMonths }} />
            </span>
            <span className={css.termCardPayment}>{formatCAD(q.payment)}</span>
            <span className={css.termCardFreq}>
              / <FormattedMessage id={`FinanceTab.frequency.${frequency}`} />
            </span>
            <span className={css.termCardApr}>
              <FormattedMessage id="FinanceTab.apr" values={{ rate: formatRate(q.rate) }} />
            </span>
          </button>
        ))}
      </div>

      <div className={css.frequencyRow}>
        {Object.keys(FREQUENCIES).map(key => (
          <button
            key={key}
            type="button"
            className={`${css.freqBtn} ${frequency === key ? css.freqBtnActive : ''}`}
            onClick={() => setFrequency(key)}
          >
            <FormattedMessage id={`FinanceTab.frequency.${key}`} />
          </button>
        ))}
      </div>

      <div className={css.inputsGrid}>
        <label className={css.inputLabel}>
          <FormattedMessage id="FinanceTab.downPaymentLabel" />
          <input
            type="number"
            min="0"
            className={css.input}
            value={downPayment}
            onChange={e => setDownPayment(Number(e.target.value) || 0)}
          />
        </label>
        <label className={css.inputLabel}>
          <FormattedMessage id="FinanceTab.tradeCreditLabel" />
          <input
            type="number"
            min="0"
            className={css.input}
            value={tradeValue}
            onChange={e => setTradeValue(Number(e.target.value) || 0)}
          />
        </label>
        <label className={css.inputLabel}>
          <FormattedMessage id="FinanceTab.tradeOwingLabel" />
          <input
            type="number"
            min="0"
            className={css.input}
            value={tradePayout}
            onChange={e => setTradePayout(Number(e.target.value) || 0)}
          />
        </label>
      </div>

      {negativeEquity > 0 ? (
        <div className={css.negativeEquityCallout}>
          <p className={css.negativeEquityLabel}>
            <FormattedMessage id="FinanceTab.negativeEquityHannahLabel" />
          </p>
          <p className={css.negativeEquityTitle}>
            <FormattedMessage id="FinanceTab.negativeEquityTitle" />
          </p>
          <div className={css.negativeEquityRow}>
            <span>
              <FormattedMessage id="FinanceTab.negativeEquityVisibleValue" />
            </span>
            <span>{formatCAD(tradeValue)}</span>
          </div>
          <div className={css.negativeEquityRow}>
            <span>
              <FormattedMessage id="FinanceTab.negativeEquityHiddenPayout" />
            </span>
            <span>{formatCAD(tradePayout)}</span>
          </div>
          <div className={`${css.negativeEquityRow} ${css.negativeEquityRowHighlight}`}>
            <span>
              <FormattedMessage id="FinanceTab.negativeEquityAmount" />
            </span>
            <span>{formatCAD(negativeEquity)}</span>
          </div>
          <div className={css.negativeEquityRow}>
            <span>
              <FormattedMessage id="FinanceTab.negativeEquityRolledIn" />
            </span>
            <span>{formatCAD(negativeEquity)}</span>
          </div>
          <div className={css.negativeEquityRow}>
            <span>
              <FormattedMessage id="FinanceTab.negativeEquityPaymentImpact" />
            </span>
            <span>
              +{formatCAD(paymentImpact)} /{' '}
              <FormattedMessage id={`FinanceTab.frequency.${frequency}`} />
            </span>
          </div>
          <p className={css.negativeEquityExplainer}>
            <FormattedMessage id="FinanceTab.negativeEquityExplainer" />
          </p>
        </div>
      ) : null}

      <div className={css.summary}>
        <div className={css.summaryRow}>
          <span>
            <FormattedMessage id="FinanceTab.estimatedTaxLabel" />
          </span>
          <span>{formatCAD(tax)}</span>
        </div>
        <div className={`${css.summaryRow} ${css.summaryRowTotal}`}>
          <span>
            <FormattedMessage id="FinanceTab.amountFinancedLabel" />
          </span>
          <span>{formatCAD(amountFinanced)}</span>
        </div>
      </div>

      <div className={css.protections}>
        {warrantyEligible ? (
          <label className={css.protectionRow}>
            <span>
              <input
                type="checkbox"
                checked={warrantyOn}
                onChange={e => setWarrantyOn(e.target.checked)}
              />{' '}
              <FormattedMessage id="FinanceTab.warranty" />
            </span>
            <span>{formatCAD(warrantyCost)}</span>
          </label>
        ) : null}
        {gapEligible ? (
          <label className={css.protectionRow}>
            <span>
              <input type="checkbox" checked={gapOn} onChange={e => setGapOn(e.target.checked)} />{' '}
              <FormattedMessage id="FinanceTab.gap" />
            </span>
            <span>{formatCAD(gapCost)}</span>
          </label>
        ) : null}
        <label className={css.protectionRow}>
          <span>
            <input
              type="checkbox"
              checked={roadsideOn}
              onChange={e => setRoadsideOn(e.target.checked)}
            />{' '}
            <FormattedMessage id="FinanceTab.roadside" />
          </span>
          <span>{formatCAD(ROADSIDE_ASSISTANCE_COST)}</span>
        </label>
        <label className={css.protectionRow}>
          <span>
            <input type="checkbox" checked={tireOn} onChange={e => setTireOn(e.target.checked)} />{' '}
            <FormattedMessage id="FinanceTab.roadHazardTire" />
          </span>
          <span>{formatCAD(ROAD_HAZARD_TIRE_COST)}</span>
        </label>
        {VEHICLE_REPLACEMENT_ENABLED ? (
          <label className={css.protectionRow}>
            <span>
              <input
                type="checkbox"
                checked={replacementOn}
                onChange={e => setReplacementOn(e.target.checked)}
              />{' '}
              <FormattedMessage id="FinanceTab.vehicleReplacement" />
            </span>
            <span>{formatCAD(VEHICLE_REPLACEMENT_COST)}</span>
          </label>
        ) : null}
      </div>

      {/* Asked only when the listing actually shows a history screen (util/listingVerification
          currentHistoryScreen). A buyer is never made to confirm reviewing a report that is not
          there, and its absence does not block a finance application. */}
      {historyScreen ? (
        <label className={css.historyRow}>
          <input
            type="checkbox"
            checked={historyConfirmed}
            onChange={e => setHistoryConfirmed(e.target.checked)}
          />{' '}
          <FormattedMessage
            id="FinanceTab.vehicleHistoryConfirmation"
            values={{
              provider: historyScreen.provider,
              date: String(historyScreen.checkedAt || '').slice(0, 10),
            }}
          />
        </label>
      ) : null}

      <PrimaryButton
        className={css.ctaButton}
        type="button"
        disabled={Boolean(historyScreen) && !historyConfirmed}
        onClick={() => setLeadFormOpen(true)}
      >
        <FormattedMessage id="FinanceTab.preApprovalCta" />
      </PrimaryButton>

      <p className={css.financingDisclaimer}>
        <FormattedMessage id="FinanceTab.financingDisclaimer" />
      </p>

      <FinanceLeadForm
        isOpen={leadFormOpen}
        onClose={() => setLeadFormOpen(false)}
        listingId={listingId}
        sellerType={sellerType}
        purchaseOption={purchaseOptionSnapshot}
        estimate={estimateSnapshot}
      />
    </div>
  );
}
